import { observer } from "mobx-react-lite";
import { cloneElement, useCallback, useEffect, useMemo } from "react";
import { useStores } from "stores/setup/use-store";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  IconButton,
  keyframes,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  AudioWaveform,
  Ban,
  Cloud,
  CloudRain,
  Gauge,
  Layers,
  Radio,
  SmartphoneCharging,
  Telescope,
  Thermometer,
  Waves,
  Wind,
} from "lucide-react";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import { useTranslation } from "react-i18next";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "components/Drawer/Drawer";
import LayerDrawerContent from "components/LayerPopoverContent/LayerDrawerContent";
import useForecastMapLayerData from "pages/ForecastChart/ForecastChartPanelMap/hooks/useForecastMapLayerData";
import useRadarData from "pages/ForecastChart/ForecastChartPanelMap/hooks/useRadarData";

interface LayersContainerButtonProps {}

const LayersContainerButton: React.FC<LayersContainerButtonProps> = () => {
  const {
    dataStores: { radarMapStore, forecastMapStore, forecastChartPageStore },
  } = useStores();

  const { t } = useTranslation("translation", {
    keyPrefix: "layer",
  });

  const companyId = localStorage.getItem("companyId") || "0";

  useRadarData();
  useForecastMapLayerData();

  const toggleEnvironmentalVariable = (variable: EnvironmentalVariables) => {
    const isAlreadySelected =
      forecastMapStore.selectedEnvironmentalVariable === variable;

    radarMapStore.setShowRadar(false);
    forecastMapStore.stop();

    if (isAlreadySelected) {
      return;
    }

    if (forecastMapStore.layersData) {
      const layersData = forecastMapStore.layersData;
      const currentIndex = forecastMapStore.currentIndex;
      const currentTime = layersData?.tiles[currentIndex]?.time;
      forecastMapStore.setCurrentTimestemp(currentTime);
    }

    forecastMapStore.setLayersData(null); // Necessary to avoid breaking the color map.
    forecastMapStore.setSelectedEnvironmentalVariable(variable);
    forecastMapStore.setShow(true);

    forecastMapStore.setDrawerLayerOpen(false);
  };

  const handleClearLayers = useCallback(() => {
    forecastMapStore.setShow(false);
    radarMapStore.setShowRadar(false);
    forecastMapStore.setCurrentTimestemp(null);
    forecastMapStore.setSelectedEnvironmentalVariable(null);
    forecastMapStore.setLayersData(null); // Necessary to avoid breaking the color map.
    forecastMapStore.setDrawerLayerOpen(false);
  }, [forecastMapStore, radarMapStore]);

  const showWeatherLayers = useMemo(() => {
    if (companyId === "84" || companyId === "88") {
      return false;
    }

    return true;
  }, [companyId]);

  const showWaveLayers = useMemo(() => {
    if (companyId === "83" || companyId === "84" || companyId === "88") {
      return false;
    }

    return true;
  }, [companyId]);

  const sections = useMemo(() => {
    const weatherItems = showWeatherLayers
      ? [
          {
            label: t("wind"),
            icon: <Wind size={18} />,
            environmentalVariable: EnvironmentalVariables.WIND_VEL,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.WIND_VEL),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.WIND_VEL,
          },
          {
            label: t("gust"),
            icon: <Wind size={18} />,
            environmentalVariable: EnvironmentalVariables.GUST,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.GUST),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.GUST,
          },
          {
            label: t("airTemp"),
            icon: <Thermometer size={18} />,
            environmentalVariable: EnvironmentalVariables.TEMP_AIR,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.TEMP_AIR),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.TEMP_AIR,
          },
          {
            label: t("visibility"),
            icon: <Telescope size={18} />,
            environmentalVariable: EnvironmentalVariables.VISIBILITY,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.VISIBILITY),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.VISIBILITY,
          },
          {
            label: t("rain"),
            icon: <CloudRain size={18} />,
            environmentalVariable: EnvironmentalVariables.PRECIP,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.PRECIP),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.PRECIP,
          },
          {
            label: t("cloudCover"),
            icon: <Cloud size={18} />,
            environmentalVariable: EnvironmentalVariables.TOTAL_CLOUD_COVER,
            onClick: () =>
              toggleEnvironmentalVariable(
                EnvironmentalVariables.TOTAL_CLOUD_COVER
              ),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.TOTAL_CLOUD_COVER,
          },
          {
            label: t("airPressure"),
            icon: <Gauge size={18} />,
            environmentalVariable: EnvironmentalVariables.SLP,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.SLP),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.SLP,
          },
        ]
      : [];

    const waveItems: {
      label: string;
      icon: JSX.Element;
      environmentalVariable: EnvironmentalVariables;
      onClick: () => void;
      isSelected: boolean;
    }[] = showWaveLayers
      ? [
          {
            label: t("waveHeight"),
            icon: <Waves size={18} />,
            environmentalVariable: EnvironmentalVariables.WAVE_HEIGHT,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.WAVE_HEIGHT),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.WAVE_HEIGHT,
          },
          {
            label: t("waveHeightMax"),
            icon: <AudioWaveform size={18} />,
            environmentalVariable: EnvironmentalVariables.WAVE_HEIGHT_MAX,
            onClick: () =>
              toggleEnvironmentalVariable(
                EnvironmentalVariables.WAVE_HEIGHT_MAX
              ),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.WAVE_HEIGHT_MAX,
          },
          {
            label: t("wavePower"),
            icon: <SmartphoneCharging size={18} />,
            environmentalVariable: EnvironmentalVariables.WAVE_POWER,
            onClick: () =>
              toggleEnvironmentalVariable(EnvironmentalVariables.WAVE_POWER),
            isSelected:
              forecastMapStore.selectedEnvironmentalVariable ===
              EnvironmentalVariables.WAVE_POWER,
          },
        ]
      : [];

    return [
      {
        title: t("weather"),
        emptyItemsText: t("noWeatherDataRegion"),
        items: weatherItems,
      },
      {
        title: t("waves"),
        emptyItemsText: t("noWaveDataRegion"),
        items: waveItems,
      },
      {
        title: t("radars"),
        items: [
          {
            label: t("weatherRadar"),
            icon: <Radio size={18} />,
            environmentalVariable: EnvironmentalVariables.RADAR,
            onClick: () => {
              const isEnablingRadar = !radarMapStore.showRadar;
              radarMapStore.setShowRadar(isEnablingRadar);

              // Set map zoom to 7 when enabling radar for optimal tile resolution
              if (isEnablingRadar) {
                forecastChartPageStore.setMapZoom(7);
              }

              forecastMapStore.stop();
              forecastMapStore.setLayersData(null);
              forecastMapStore.setSelectedEnvironmentalVariable(null);
              forecastMapStore.setShow(false);
              forecastMapStore.setDrawerLayerOpen(false);
            },
            isSelected: radarMapStore.showRadar,
          },
        ],
      },
    ];
  }, [
    t,
    showWaveLayers,
    showWeatherLayers,
    forecastMapStore.selectedEnvironmentalVariable,
    radarMapStore.showRadar,
    forecastMapStore.show,
    forecastChartPageStore,
  ]);

  const selectedItem = useMemo(() => {
    for (const section of sections) {
      for (const item of section.items) {
        if (item.isSelected) {
          return item;
        }
      }
    }
    return null;
  }, [sections]);

  const hasLayer = useMemo(() => {
    return (
      forecastMapStore.selectedEnvironmentalVariable !== null ||
      radarMapStore.showRadar === true
    );
  }, [forecastMapStore.selectedEnvironmentalVariable, radarMapStore.showRadar]);

  return (
    <Box>
      {hasLayer ? (
        <Tooltip label={t("tooltipRemoveLayer")} placement="top" hasArrow>
          <IconButton
            aria-label={"Remove Layer"}
            icon={<CloseIcon style={{ width: "16px", height: "16px" }} />}
            size="x-small"
            variant="solid"
            color="red.500"
            top={"10px"}
            right="-85px"
            onClick={() => handleClearLayers()}
            _hover={{ bg: "red", color: "white" }}
          />
        </Tooltip>
      ) : null}

      <Drawer
        isOpen={forecastMapStore.drawerLayerOpen}
        onClose={() => forecastMapStore.setDrawerLayerOpen(false)}
        title={t("selectLayer") as string}
        titleColor="#fff"
        titleBackground="newBlue.500"
        placement="left"
        footerStyle={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "40px",
          background: "#e53e3e",
          cursor: "pointer",
          color: "white",
          gap: 3,
          _hover: { bg: "#ed5d5d" },
          onClick: () => handleClearLayers(),
        }}
        footerChildren={
          hasLayer ? (
            <>
              <Ban size={18} style={{ verticalAlign: "middle" }} />
              <Text mt="3px">{t("tooltipRemoveLayer")} </Text>
            </>
          ) : null
        }
      >
        <Alert
          rounded="base"
          variant="left-accent"
          bg="#bee3f8"
          status="info"
          color="gray.700"
        >
          <AlertIcon />
          <AlertDescription fontSize={"smaller"}>
            {t("maplayerDisclaimer")}
          </AlertDescription>
        </Alert>
        <LayerDrawerContent content={sections} />
      </Drawer>

      <Center
        bg={hasLayer ? "#286795" : "rgba(0, 0, 0, 0.4)"}
        w="75px"
        h="65px"
        borderRadius="4px"
        ml="21px"
        color={"#fff"}
        flexDirection="column"
        cursor="pointer"
        _hover={{
          bg: hasLayer ? "rgb(72, 132, 175)" : "rgba(0, 0, 0, 0.47)",
        }}
        border={hasLayer ? "1px solid rgb(228, 228, 228)" : ""}
        onClick={() => forecastMapStore.setDrawerLayerOpen(true)}
      >
        {hasLayer ? (
          selectedItem?.icon ? (
            cloneElement(selectedItem.icon, { color: "#fff", size: "20px" })
          ) : (
            <Layers size={20} />
          )
        ) : (
          <Layers size={20} />
        )}
        <Tooltip
          label={selectedItem?.label}
          isDisabled={!selectedItem || !hasLayer}
        >
          <Text
            fontSize="xs"
            px="2px"
            whiteSpace="normal"
            overflow="hidden"
            textOverflow="ellipsis"
            noOfLines={2}
            textAlign="center"
            wordBreak="break-word"
          >
            {hasLayer ? selectedItem?.label ?? t("layers") : t("layers")}
          </Text>
        </Tooltip>
      </Center>
    </Box>
  );
};

export default observer(LayersContainerButton);
